<script lang="ts">
  import {
    getCachedWorkOrderSelection,
    searchWorkOrderSelections,
    normalizeId,
    normalizeSearchInput,
    searchLimit,
    mapSelectionToItem,
    uniqueSelections,
    type WorkOrderSelectItem,
    type WorkOrderSelectSearchMode,
  } from "./_logic/selection-cache"
  import type { WorkOrderSelectSelection } from "../../_logic/work-orders-selection"
  import AsyncSelectInput from "@kontextwerk/web-sdk/ui/forms/AsyncSelectInput.svelte"
  type Props = {
    organizationId: string
    disabled?: boolean
    brightBackground?: boolean
    surfaceTone?: "light" | "dark"
    id?: string
    placeholder?: string
    label?: string | null
    searchMode?: WorkOrderSelectSearchMode
    requiredAccess?: "view" | "manage"
    employeeId?: string | null
    defaultPropertyId?: string | null
    emptyLabel?: string
    preferredSelections?: WorkOrderSelectSelection[]
    onChange?: (selection: WorkOrderSelectSelection | null) => void
  }

  type PublicProps = Props & {
    value?: string
    selectedWorkOrder?: WorkOrderSelectSelection | null
  }

  let {
    organizationId,
    disabled = false,
    brightBackground = true,
    surfaceTone = "light",
    id = "work-order-async-select",
    placeholder = "Auftrag auswählen",
    label = null,
    searchMode = "default",
    requiredAccess = "view",
    employeeId = null,
    defaultPropertyId = null,
    emptyLabel = "Keine passenden Aufträge gefunden",
    preferredSelections = [],
    onChange = undefined,
    value = $bindable(""),
    selectedWorkOrder = $bindable<WorkOrderSelectSelection | null>(null),
  }: PublicProps = $props()

  const currentWorkOrderId = $derived(normalizeId(value))
  const currentSelection = $derived.by(() => {
    const cachedSelection = getCachedWorkOrderSelection(organizationId, currentWorkOrderId)

    if (cachedSelection) return cachedSelection

    if (selectedWorkOrder && normalizeId(selectedWorkOrder.id) === currentWorkOrderId)
      return selectedWorkOrder

    return null
  })
  const selectedItem = $derived(currentSelection ? mapSelectionToItem(currentSelection) : null)
  const preferredItems = $derived(uniqueSelections(preferredSelections).map(mapSelectionToItem))
  let selectedOption = $state<WorkOrderSelectItem | null>(null)

  const sameOption = (left: WorkOrderSelectItem | null, right: WorkOrderSelectItem | null) =>
    normalizeId(left?.value) === normalizeId(right?.value) &&
    (left?.label ?? "") === (right?.label ?? "") &&
    (left?.subLabel ?? "") === (right?.subLabel ?? "") &&
    (left?.metaLabel ?? "") === (right?.metaLabel ?? "")

  $effect(() => {
    if (!sameOption(selectedOption, selectedItem)) selectedOption = selectedItem
  })

  const loadOptions = async (input: string) => {
    try {
      const normalizedInput = normalizeSearchInput(input)

      if (!normalizedInput && preferredItems.length) return preferredItems

      const nextSelections = await searchWorkOrderSelections({
        organizationId,
        input,
        limit: searchLimit,
        searchMode,
        requiredAccess,
        employeeId,
        propertyId: normalizedInput ? null : defaultPropertyId,
      })

      return nextSelections.map(mapSelectionToItem)
    } catch (err) {
      console.error("work_order_async_select_search_failed", err)

      return []
    }
  }

  const applySelection = (nextSelection: WorkOrderSelectSelection | null) => {
    const nextWorkOrderId = normalizeId(nextSelection?.id)
    selectedWorkOrder = nextSelection
    value = nextWorkOrderId
    selectedOption = nextSelection ? mapSelectionToItem(nextSelection) : null
    onChange?.(nextSelection)
  }

  const handleSelectionChange = (option: unknown) => {
    const item = option && typeof option === "object" ? (option as WorkOrderSelectItem) : null
    const nextValue = normalizeId(item?.value)

    if (!nextValue) {
      applySelection(null)

      return
    }

    const selected = item?.selection ?? getCachedWorkOrderSelection(organizationId, nextValue)

    if (!selected) {
      value = nextValue

      if (item) selectedOption = item

      console.warn("work_order_async_select_selection_missing", {
        organizationId,
        workOrderId: nextValue,
      })

      return
    }

    applySelection(selected)
  }
</script>

{#snippet selectionRenderer({ selection })}
  {@const option = selection as WorkOrderSelectItem | null}
  <div class="select-selection">
    <span class="select-selection__label">{option?.label ?? option?.value ?? ""}</span>
    {#if option?.subLabel || option?.metaLabel}
      <span class="select-selection__meta">
        {[option.subLabel, option.metaLabel].filter(Boolean).join(" · ")}
      </span>
    {/if}
  </div>
{/snippet}

{#snippet itemRenderer({ item })}
  {@const option = item as WorkOrderSelectItem}
  <div class="work-order-option">
    <span class="work-order-option__title">{option.label ?? option.value}</span>
    {#if option.subLabel || option.metaLabel}
      <span class="work-order-option__meta">
        {[option.subLabel, option.metaLabel].filter(Boolean).join(" · ")}
      </span>
    {/if}
  </div>
{/snippet}

<div class="work-order-async-select">
  {#if label}
    <span class="work-order-async-select__label">{label}</span>
  {/if}
  <AsyncSelectInput
    bind:value
    bind:selectedOption
    {id}
    {disabled}
    {loadOptions}
    {brightBackground}
    {surfaceTone}
    {placeholder}
    ariaLabel={label ?? placeholder}
    {emptyLabel}
    loadingLabel="Suche läuft…"
    onSelectionChange={handleSelectionChange}
    {selectionRenderer}
    {itemRenderer}
  />
</div>

<style lang="less">
  .work-order-async-select {
    display: flex;
    flex-direction: column;
    gap: 0.28rem;
    min-width: 0;
    width: 100%;
  }

  .work-order-async-select__label {
    color: var(--text-100);
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1.15;
    padding-left: 0.8rem;
    text-transform: uppercase;
  }

  .select-selection {
    min-width: 0;
    color: var(--text-100);
    font-weight: 650;
    display: grid;
    gap: 0.1rem;
  }

  .select-selection__label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .select-selection__meta {
    color: color-mix(in srgb, var(--text-100) 68%, transparent);
    display: block;
    font-size: 0.78rem;
    font-weight: 500;
    line-height: 1.15;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .work-order-option {
    display: grid;
    gap: 0.15rem;
    min-width: 0;
    width: 100%;
    padding: 0.18rem 0;
    color: var(--text-100);
  }

  .work-order-option__title {
    color: var(--text-100);
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .work-order-option__meta {
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
    font-size: 0.82rem;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
